import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@/components/shared/Card';
import Button from '@/components/shared/Button';
import Modal from '@/components/shared/Modal';
import { useSuppliers, getPriceStatus } from '@/hooks/useSuppliers';
import { PriceForm } from '@/pages/internal/SupplierDetail';
import { formatDate, formatPriceCt } from '@/utils/formatters';
import {
  FiPlus, FiTrash2, FiRefreshCw, FiSearch, FiAlertCircle, FiX,
  FiDollarSign, FiClock, FiCheckCircle, FiCalendar,
} from 'react-icons/fi';

/**
 * Preisuebersicht: alle Einkaufspreise ueber alle Versorger hinweg.
 * Daten kommen ausschliesslich vom Server (/api/supplier-prices).
 */

const STATUS_META = {
  aktiv:      { label: 'Aktiv',      cls: 'bg-green-100 text-green-700' },
  geplant:    { label: 'Geplant',    cls: 'bg-blue-100 text-blue-700' },
  abgelaufen: { label: 'Abgelaufen', cls: 'bg-red-100 text-red-700' },
  inaktiv:    { label: 'Inaktiv',    cls: 'bg-gray-100 text-gray-600' },
};

const fmtKwh = (n) => (n == null ? null : Number(n).toLocaleString('de-DE'));

const consumptionBand = (price) => {
  const min = price.min_consumption_kwh;
  const max = price.max_consumption_kwh;
  if (min == null && max == null) return 'Alle Verbraeuche';
  if (min != null && max != null) return `${fmtKwh(min)} – ${fmtKwh(max)} kWh`;
  if (min != null) return `ab ${fmtKwh(min)} kWh`;
  return `bis ${fmtKwh(max)} kWh`;
};

const PriceEntry = () => {
  const navigate = useNavigate();
  // loadError = Liste ist nicht (vollstaendig) da. Schreibfehler kommen als
  // geworfener Fehler zurueck und landen in formError/actionError — sie duerfen
  // die Liste nicht als "nicht geladen" darstellen und nichts blockieren.
  const {
    suppliers, prices, marginSettings, loading, loadError, reload, addPrice, deletePrice,
  } = useSuppliers();

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [actionError, setActionError] = useState('');
  const [search, setSearch] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('alle');
  const [statusFilter, setStatusFilter] = useState('alle');

  const supplierName = useMemo(() => {
    const map = new Map();
    suppliers.forEach((s) => map.set(String(s.id), s.name));
    return (id) => map.get(String(id)) || `Versorger #${id}`;
  }, [suppliers]);

  const rows = useMemo(
    () => prices
      .map((p) => ({ ...p, _status: getPriceStatus(p), _supplier: supplierName(p.supplier_id) }))
      .sort((a, b) => (a._supplier || '').localeCompare(b._supplier || '', 'de')
        || (a.price_ct_kwh ?? 0) - (b.price_ct_kwh ?? 0)),
    [prices, supplierName]
  );

  const stats = useMemo(() => {
    const s = { gesamt: rows.length, aktiv: 0, geplant: 0, abgelaufen: 0 };
    rows.forEach((r) => { if (s[r._status] != null) s[r._status] += 1; });
    return s;
  }, [rows]);

  const visible = rows.filter((r) => {
    if (supplierFilter !== 'alle' && String(r.supplier_id) !== String(supplierFilter)) return false;
    if (statusFilter !== 'alle' && r._status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!`${r.label || ''} ${r._supplier || ''}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const handleSave = async (data) => {
    setSaving(true);
    setFormError('');
    try {
      await addPrice(data);
      setShowModal(false);
    } catch (err) {
      setFormError(err.message || 'Preis konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (price) => {
    if (!window.confirm(`Preis "${price.label}" (${price._supplier}) loeschen? Er fliesst danach in keine Kalkulation mehr ein.`)) return;
    setActionError('');
    try {
      await deletePrice(price.id);
    } catch (err) {
      setActionError(err.message || 'Preis konnte nicht geloescht werden');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Einkaufspreise</h1>
          <p className="text-gray-500 mt-1">
            {loading
              ? 'Lade Preise …'
              : loadError
                ? 'Preise konnten nicht geladen werden'
                : `${stats.gesamt} Preise von ${suppliers.length} Versorgern · Standard-Marge ${marginSettings.default_margin_ct_kwh} ct/kWh`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" icon={FiRefreshCw} onClick={reload} disabled={loading}>Aktualisieren</Button>
          {/* NIE an einen Schreibfehler koppeln — sonst waere Anlegen nach einem
              gescheiterten Versuch dauerhaft gesperrt. Gesperrt nur, solange
              geladen wird oder gar keine Daten da sind (dann fehlen auch die
              Versorger zur Auswahl). */}
          <Button
            icon={FiPlus}
            onClick={() => { setFormError(''); setShowModal(true); }}
            disabled={loading || !!loadError}
          >
            Preis anlegen
          </Button>
        </div>
      </div>

      {loadError && (
        <Card className="bg-red-50 border-red-200">
          <div className="flex items-start gap-3">
            <FiAlertCircle className="text-red-600 mt-0.5 shrink-0" size={18} />
            <div>
              <p className="font-semibold text-red-800">Preise konnten nicht geladen werden</p>
              <p className="text-sm text-red-700 mt-1">{loadError}</p>
              <Button size="sm" className="mt-3" onClick={reload}>Erneut versuchen</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Schreibfehler (Loeschen): die Liste unten bleibt gueltig und bedienbar. */}
      {actionError && (
        <Card className="bg-red-50 border-red-200">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-red-800">Aktion wurde nicht ausgefuehrt</p>
              <p className="text-sm text-red-700 mt-0.5">{actionError}</p>
            </div>
            <button
              onClick={() => setActionError('')}
              className="p-1 text-red-400 hover:text-red-700 rounded"
              title="Hinweis ausblenden"
            >
              <FiX size={16} />
            </button>
          </div>
        </Card>
      )}

      {/* Kennzahlen = zugleich Statusfilter */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { key: 'alle', label: 'Alle Preise', value: stats.gesamt, icon: FiDollarSign },
          { key: 'aktiv', label: 'Aktiv', value: stats.aktiv, icon: FiCheckCircle },
          { key: 'geplant', label: 'Geplant', value: stats.geplant, icon: FiCalendar },
          { key: 'abgelaufen', label: 'Abgelaufen', value: stats.abgelaufen, icon: FiClock },
        ].map((k) => (
          <button
            key={k.key}
            onClick={() => setStatusFilter(k.key)}
            className={`text-left rounded-xl border p-4 transition-colors ${
              statusFilter === k.key ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2 text-gray-500">
              <k.icon size={15} />
              <span className="text-sm">{k.label}</span>
            </div>
            {/* Bei Ladefehler "–" statt "0" — eine Null laese sich als
                "es gibt keine Preise" missverstehen. */}
            <p className="text-2xl font-bold mt-1 text-gray-900">{loading || loadError ? '–' : k.value}</p>
          </button>
        ))}
      </div>

      {!loading && !loadError && stats.abgelaufen > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <p className="text-sm text-amber-800">
            {stats.abgelaufen} {stats.abgelaufen === 1 ? 'Preis ist' : 'Preise sind'} abgelaufen.
            Bitte Gueltigkeit aktualisieren (neu anlegen) oder loeschen.
          </p>
        </Card>
      )}

      <Card padding={false}>
        <div className="p-4 border-b border-gray-200 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <FiSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tarif oder Versorger suchen …"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
          </div>
          <select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="alle">Alle Versorger</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <p className="text-sm text-gray-400 py-12 text-center">Lade Preise …</p>
          ) : visible.length === 0 ? (
            <div className="py-12 text-center">
              <FiDollarSign className="mx-auto text-gray-300 mb-3" size={32} />
              <p className="text-gray-600 font-medium">
                {loadError
                  ? 'Keine Daten verfuegbar'
                  : stats.gesamt === 0
                    ? 'Noch keine Einkaufspreise hinterlegt'
                    : 'Kein Preis passt zu den Filtern'}
              </p>
              {!loadError && stats.gesamt === 0 && (
                suppliers.length === 0 ? (
                  <>
                    <p className="text-sm text-gray-500 mt-1">Lege zuerst einen Versorger an.</p>
                    <Button className="mt-3" onClick={() => navigate('/app/versorger')}>Zu den Versorgern</Button>
                  </>
                ) : (
                  <Button className="mt-3" icon={FiPlus} onClick={() => { setFormError(''); setShowModal(true); }}>
                    Ersten Preis anlegen
                  </Button>
                )
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 bg-gray-50">
                  <th className="px-4 py-3">Versorger</th>
                  <th className="px-4 py-3">Bezeichnung</th>
                  <th className="px-4 py-3 text-right">Preis ct/kWh</th>
                  <th className="px-4 py-3 text-right">Laufzeit</th>
                  <th className="px-4 py-3">Verbrauchsband</th>
                  <th className="px-4 py-3">Gueltig bis</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map((p) => {
                  const st = STATUS_META[p._status];
                  return (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/app/versorger/${p.supplier_id}`)}
                          className="text-primary-700 hover:underline font-medium"
                        >
                          {p._supplier}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-900">{p.label}</span>
                        {p.company_id && (
                          <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">Kundenspez.</span>
                        )}
                        <span className="ml-2 px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                          {p.price_type === 'fixed' ? 'Fest' : p.price_type === 'indexed' ? 'Index' : 'Dynamisch'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">{formatPriceCt(p.price_ct_kwh)}</td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {p.contract_duration_months ? `${p.contract_duration_months} Mon.` : '–'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{consumptionBand(p)}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {p.valid_until ? formatDate(p.valid_until) : 'unbefristet'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${st.cls}`}>{st.label}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDelete(p)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
                          title="Preis loeschen"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <Card>
        <Card.Title>Wofuer diese Preise gelten</Card.Title>
        <p className="text-sm text-gray-500 mt-2">
          Hier gepflegte Einkaufspreise sind die Grundlage des Kalkulator-Modus &bdquo;Versorgerpreise&ldquo;.
          Der Standardmodus rechnet dagegen boersenbasiert mit EEX-Futures und Strukturaufschlag.
          Kundenspezifische Sondertarife (mit company_id) gehoeren nur in die Kalkulation der jeweiligen Firma,
          abgelaufene Preise in gar keine.
        </p>
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Einkaufspreis anlegen" size="lg">
        <PriceForm
          suppliers={suppliers}
          onSave={handleSave}
          onCancel={() => setShowModal(false)}
          saving={saving}
          error={formError}
        />
      </Modal>
    </div>
  );
};

export default PriceEntry;
